/**
 * OpenPostsPanel — every gap in one screen rather than seven.
 *
 * Unfilled shifts across the jobs this caller can see, for a date range,
 * because "what is not covered" is the question a manager asks on a Friday
 * afternoon and answering it by paging through seven days is how a post ends
 * up uncovered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { schedulingAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { UserX } from 'lucide-react';

export function OpenPostsPanel({ weekStart, jobId, onOpenWeek }) {
  const [start, setStart] = useState(weekStart);
  const [end, setEnd] = useState(() => {
    const d = new Date(`${weekStart}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 13);
    return d.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('job');   // job | all

  const load = useCallback(() => {
    setLoading(true);
    schedulingAPI.openPosts({
      start_date: start, end_date: end,
      job_id: scope === 'job' ? jobId : undefined,
    }).then(({ data }) => setRows(data.open_posts || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [start, end, jobId, scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="open-posts-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-fg-muted">From</label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="mt-1 h-9 w-40" data-testid="open-posts-start" />
        </div>
        <div>
          <label className="text-xs text-fg-muted">To</label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="mt-1 h-9 w-40" data-testid="open-posts-end" />
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface-sunken p-1">
          {[['job', 'This job'], ['all', 'Every job I can see']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setScope(v)}
              data-testid={`open-posts-scope-${v}`}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                scope === v ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-fg-muted" data-testid="open-posts-loading">Loading…</p>
      ) : !rows.length ? (
        <div className="rounded-xl border border-line bg-surface-sunken px-6 py-10 text-center"
          data-testid="open-posts-empty">
          <UserX className="h-8 w-8 mx-auto text-fg-muted" />
          <p className="text-base md:text-lg text-fg mt-3">Nothing unfilled in that range</p>
          <p className="text-sm text-fg-muted mt-1">Every shift has an officer on it.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-line overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr>
                {['Date', 'Job', 'Post', 'Window', 'Reason'].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wide
                                         text-fg-muted border-b border-line">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-line-subtle last:border-0
                                          hover:bg-surface-sunken/60"
                  data-testid={`open-post-row-${r.id}`}>
                  <td className="px-3 py-2 tabular-nums text-fg">{r.shift_date}</td>
                  <td className="px-3 py-2">
                    <Button variant="link" className="h-auto p-0 text-sm"
                      onClick={() => onOpenWeek(r.job_id, r.shift_date)}
                      data-testid={`open-post-goto-${r.id}`}>
                      {r.job_number || '—'}
                    </Button>
                  </td>
                  <td className="px-3 py-2 text-fg">{r.post_name || '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-fg-muted">{r.window}</td>
                  <td className="px-3 py-2 text-fg-muted">{r.open_post_reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
